// ProjectsOverviewPage.jsx — one-page overview of every project.
// Location: src/pages/projects/ProjectsOverviewPage.jsx
//
// A dense, at-a-glance review table for projects — the counterpart to the Team
// Overview page, and built with the same table machinery:
//   - Every field a column; scalar fields edit inline. The structured fields
//     (retainer, fee scopes) are edited in a per-project detail card opened from
//     the "Details" column or the project name; fee allocation and stage
//     timeline stay read-only in that card because they are owned by the Stages
//     and Timeline pages that drive invoicing.
//   - Multi-rule sort + multi-rule filter + free-text search, AND per-column
//     controls that mirror the Team Overview: click a header to sort, drag
//     headers to reorder, freeze/unfreeze a column (sticky), collapse a column.
//     Column order / frozen / collapsed state persist in localStorage.
//   - Site details: elevation (auto-filled via Open-Meteo), Köppen climate zone
//     (auto-suggested from coordinates), site type tags, and regional
//     subsurface context — soil texture and bedrock lithology fetched per site
//     (see utils/subsurface.js). That last one is REGIONAL context, never site
//     data: a 250 m soil grid and a mapped geological unit describe the area,
//     not the plot, so the column header, tooltips and detail card all say so.
//
// Role visibility: the Fee and currency columns, and the fee-editing sections of
// the detail card, are admin-only (hidden from supervisors).
//
// Fee safety: any edit that changes a project's total fee (base fee / retainer /
// fee scopes) records a fee revision via the same path and formula the edit
// modal uses (see utils/projectFee.calculateTotalFee), so the Invoices page and
// fee history stay in sync — nothing downstream breaks.
//
// Additive: this does not touch the existing Projects page.

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  ArrowUpDown, Filter, X, Search, ExternalLink, Mountain, Check, Pencil, Plus, Trash2, CloudRain, Layers,
  Info, RotateCcw,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Lock, Unlock,
} from 'lucide-react';
import { parseCoordinates } from './ProjectMapPage';
import { SITE_TYPES, siteTypeLabel, KOPPEN_ZONES, koppenLabel, suggestKoppen } from '../../utils/siteClassification';
import { fetchElevations } from '../../utils/elevation';
import { fetchClimateNormals } from '../../utils/climateNormals';
import {
  fetchSubsurface, describeSoilSlice, bedrockSourceTerms,
  resolveSoil, resolveBedrock, withOverride,
} from '../../utils/subsurface';
import { calculateTotalFee } from '../../utils/projectFee';

// ══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════════
const BRIO_GOLD = '#9f902a';
const BRIO_GREEN = '#689581';
const BRIO_RED = '#934948';
const BRIO_GREY = '#666666';
const BRIO_BORDER = '#e5e5e5';
const BRIO_BG = '#fafaf8';
const FONT = 'Raleway, sans-serif';

const STATUS_OPTIONS = ['active', 'paused', 'completed', 'archived'];
const CURRENCY_OPTIONS = ['INR', 'EUR'];
const CURRENCY_SYMBOL = { INR: '₹', EUR: '€' };
const SITE_TYPE_OPTIONS = SITE_TYPES.map(t => ({ value: t.value, label: t.label }));
// Roles that may be assigned as a project supervisor, and may see/record fees.
const SUPERVISOR_ROLES = ['admin', 'admin-pa', 'supervisor'];

// ── Column definitions ──────────────────────────────────────────────────────
// flexW = normal width, minW = floor, alwaysFrozen = permanently sticky-left,
// hideForSupervisor = admin-only (fee-related).
const COLUMNS = [
  { key: 'name',        label: 'Project',        sortable: true,  filterable: false, minW: 150, flexW: 200, alwaysFrozen: true },
  { key: 'code',        label: 'Code',           sortable: true,  filterable: true,  minW: 56,  flexW: 74 },
  { key: 'client',      label: 'Client',         sortable: true,  filterable: true,  minW: 110, flexW: 150 },
  { key: 'category',    label: 'Type',           sortable: true,  filterable: true,  minW: 90,  flexW: 112 },
  { key: 'status',      label: 'Status',         sortable: true,  filterable: true,  minW: 84,  flexW: 106 },
  { key: 'supervisors', label: 'Supervisors',    sortable: false, filterable: false, minW: 110, flexW: 150 },
  { key: 'currency',    label: 'Curr.',          sortable: true,  filterable: true,  minW: 52,  flexW: 66,  hideForSupervisor: true },
  { key: 'budget',      label: 'Fee',            sortable: true,  filterable: false, minW: 78,  flexW: 96,  num: true, hideForSupervisor: true },
  { key: 'targetCB',    label: 'Target constr.', sortable: true,  filterable: false, minW: 92,  flexW: 112, num: true },
  { key: 'finalCB',     label: 'Final constr.',  sortable: true,  filterable: false, minW: 92,  flexW: 112, num: true },
  { key: 'projectSize', label: 'Built (m²)',     sortable: true,  filterable: false, minW: 74,  flexW: 92,  num: true },
  { key: 'plotArea',    label: 'Plot (m²)',      sortable: true,  filterable: false, minW: 74,  flexW: 92,  num: true },
  { key: 'plannedHours',label: 'Plan hrs',       sortable: true,  filterable: false, minW: 66,  flexW: 82,  num: true },
  { key: 'timeline',    label: 'Timeline',       sortable: true,  filterable: false, minW: 120, flexW: 150 },
  { key: 'address',     label: 'Location',       sortable: false, filterable: false, minW: 120, flexW: 160 },
  // The readable location. Location above holds coordinates, which are neither
  // legible here nor usable in a filename; these two feed the PROJECT-LOCATION
  // field of press-kit names. Filterable because checking a whole cluster at
  // once — every project that came out "Alibaug" — is how these get verified.
  { key: 'city',        label: 'City',           sortable: true,  filterable: true,  minW: 90,  flexW: 120 },
  { key: 'country',     label: 'Country',        sortable: true,  filterable: true,  minW: 80,  flexW: 100 },
  { key: 'elevation',   label: 'Elev (m)',       sortable: true,  filterable: false, minW: 78,  flexW: 96,  num: true },
  { key: 'climate',     label: 'Climate',        sortable: true,  filterable: true,  minW: 100, flexW: 130 },
  { key: 'siteTypes',   label: 'Site type',      sortable: false, filterable: true,  minW: 130, flexW: 185 },
  { key: 'climateTemp', label: 'Temp °C (W/S)',  sortable: true,  filterable: false, minW: 120, flexW: 148,
    tooltip: 'Average seasonal temperature, °C. W = winter, S = summer (local to each site). Each pair reads day/night = average daily high / average daily low, from ~10 years of Open-Meteo (ERA5) data.' },
  { key: 'climatePrecip', label: 'Rain mm (W/S)', sortable: true, filterable: false, minW: 96,  flexW: 116,
    tooltip: 'Average seasonal rainfall in millimetres. W = winter, S = summer (local to each site) — the mean total precipitation per season, from ~10 years of Open-Meteo (ERA5) data.' },
  { key: 'soil',        label: 'Soil (0–2 m)',   sortable: true,  filterable: true,  minW: 104, flexW: 132,
    tooltip: 'Modelled soil texture at the site, from the SoilGrids 250 m global grid: the USDA class at 0–5 cm, with the 100–200 cm slice in the tooltip. Regional context — not a soil investigation.' },
  { key: 'bedrock',     label: 'Bedrock',        sortable: true,  filterable: true,  minW: 110, flexW: 150,
    tooltip: 'The geologic unit mapped at the site by Macrostrat: dominant lithology and age. Mapped at regional scale (1:250k–1:1M) — context for a conversation, never a substitute for a borehole log.' },
  { key: 'invoiceName', label: 'Invoice name',   sortable: false, filterable: false, minW: 110, flexW: 150 },
  { key: 'email',       label: 'Project email',  sortable: false, filterable: false, minW: 120, flexW: 160 },
  { key: 'comms',       label: 'Comms',          sortable: true,  filterable: true,  minW: 58,  flexW: 74 },
  { key: 'updated',     label: 'Updated',        sortable: true,  filterable: false, minW: 74,  flexW: 92 },
  { key: 'details',     label: 'Details',        sortable: false, filterable: false, minW: 60,  flexW: 74 },
];

// ── Column layout persistence (order / frozen / collapsed) ──────────────────
const COL_ORDER_KEY = 'poverview-col-order';
const FROZEN_COLS_KEY = 'poverview-frozen-cols';
const COLLAPSED_KEY = 'poverview-collapsed-cols';
const COL_WIDTHS_KEY = 'poverview-col-widths';
const COLLAPSED_W = 20; // width of a collapsed column (just the expand chevron)

// A user width override for a column, else its default flexW.
function colWidthOf(col, colWidths) {
  const w = colWidths && colWidths[col.key];
  return Number.isFinite(w) ? Math.max(col.minW, w) : col.flexW;
}

// Loosened: return any array of string keys (or null). getOrderedColumns
// reconciles it against the current role-visible column set, so a stored order
// that predates a column change (or a different role) is never discarded.
function loadStoredColumnOrder() {
  try {
    const saved = localStorage.getItem(COL_ORDER_KEY);
    if (!saved) return null;
    const order = JSON.parse(saved);
    if (!Array.isArray(order) || !order.every(k => typeof k === 'string')) return null;
    return order;
  } catch { return null; }
}
function loadFrozenCols() {
  try { const s = localStorage.getItem(FROZEN_COLS_KEY); return s ? JSON.parse(s) : {}; } catch { return {}; }
}
function loadCollapsedCols() {
  try { const s = localStorage.getItem(COLLAPSED_KEY); return s ? JSON.parse(s) : {}; } catch { return {}; }
}
function loadColWidths() {
  try { const s = localStorage.getItem(COL_WIDTHS_KEY); const o = s ? JSON.parse(s) : {}; return (o && typeof o === 'object') ? o : {}; } catch { return {}; }
}

// Ordered columns (from the role-visible `baseColumns`) with frozen ones pulled
// to the left after alwaysFrozen. Tolerant of a stored order that references
// unknown/removed keys or is missing newly-added ones.
function getOrderedColumns(order, frozenCols, baseColumns) {
  const map = {};
  baseColumns.forEach(c => { map[c.key] = c; });
  let cols;
  if (!order) {
    cols = [...baseColumns];
  } else {
    const seen = new Set();
    cols = [];
    order.forEach(k => { if (map[k] && !seen.has(k)) { cols.push(map[k]); seen.add(k); } });
    baseColumns.forEach(c => { if (!seen.has(c.key)) cols.push(c); });
  }
  const alwaysFrozen = cols.filter(c => c.alwaysFrozen);
  const userFrozen = cols.filter(c => !c.alwaysFrozen && frozenCols[c.key]);
  const rest = cols.filter(c => !c.alwaysFrozen && !frozenCols[c.key]);
  return [...alwaysFrozen, ...userFrozen, ...rest];
}

// Left offset (px) for a frozen column = sum of frozen widths before it.
function computeFrozenLeft(columns, collapsedCols, frozenCols, colWidths, colIdx) {
  let left = 0;
  for (let i = 0; i < colIdx; i++) {
    const c = columns[i];
    const isFr = c.alwaysFrozen || frozenCols[c.key];
    if (!isFr) continue;
    left += collapsedCols[c.key] ? COLLAPSED_W : colWidthOf(c, colWidths);
  }
  return left;
}

// ══════════════════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════════════════
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
}

// Compact money: INR → L/Cr, EUR → k/M. Keeps the table dense.
function fmtMoney(amount, currency) {
  const n = Number(amount);
  if (!n || isNaN(n)) return '—';
  const sym = CURRENCY_SYMBOL[currency] || '';
  if (currency === 'INR') {
    if (n >= 1e7) return `${sym}${(n / 1e7).toFixed(2)} Cr`;
    if (n >= 1e5) return `${sym}${(n / 1e5).toFixed(1)} L`;
    return `${sym}${n.toLocaleString('en-IN')}`;
  }
  if (n >= 1e6) return `${sym}${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${sym}${(n / 1e3).toFixed(0)}k`;
  return `${sym}${n.toLocaleString()}`;
}

function fmtNum(n) {
  if (n == null || n === '' || isNaN(Number(n))) return '—';
  return Number(n).toLocaleString();
}

// Recursively collect ISO dates from any *date key in the stage_timeline JSON.
function collectTimelineDates(node, out = []) {
  if (!node || typeof node !== 'object') return out;
  if (Array.isArray(node)) { node.forEach(n => collectTimelineDates(n, out)); return out; }
  Object.entries(node).forEach(([k, v]) => {
    if (typeof v === 'string' && /date$/i.test(k) && /^\d{4}-\d{2}-\d{2}/.test(v)) out.push(v.slice(0, 10));
    else if (v && typeof v === 'object') collectTimelineDates(v, out);
  });
  return out;
}

// ── Human-readable rendering of the stage_timeline / budget_allocation blobs ──
// Stage keys look like "<phase>_<discipline>" (sd_ad, dd_id, aoc_ad), plus a few
// standalone stages (advance, completion, gfc1/2) and custom_NN slots.
const STAGE_TOKENS = { sd: 'SD', dd: 'DD', cd: 'CD', td: 'TD', gfc: 'GFC', gfc1: 'GFC 1', gfc2: 'GFC 2', aoc: 'AOC', cc: 'CC', ad: 'Arch', id: 'Interior', advance: 'Advance', completion: 'Completion' };
function prettyStageKey(key) {
  if (!key) return '';
  if (/^custom_\d+$/i.test(key)) return 'Custom ' + key.split('_')[1];
  if (/^misc/i.test(key)) return 'Misc';
  // Drop empty tokens and random id suffixes (letters+digits, ≥8 chars) like
  // "mt17s0fv3jmwu"; keep real words like "completion".
  const label = key.split('_')
    .filter(t => t && !/^(?=.*[a-z])(?=.*\d)[a-z0-9]{8,}$/i.test(t))
    .map(t => STAGE_TOKENS[t.toLowerCase()] || (t.charAt(0).toUpperCase() + t.slice(1)))
    .join(' ');
  return label || key;
}
// budget_allocation is a flat { stageKey: percent } map. Keep only numeric rows.
function allocationRows(ba) {
  if (!ba || typeof ba !== 'object') return [];
  return Object.entries(ba)
    .filter(([, v]) => Number.isFinite(Number(v)) && v !== null && v !== '')
    .map(([key, v]) => ({ key, label: prettyStageKey(key), pct: Number(v) }))
    .sort((a, b) => b.pct - a.pct);
}
// stage_timeline mixes real stage entries ({ startDate, targetDate }) with a lot
// of bookkeeping keys (start_*, closure_*, invoicing_*, cc__*, _rows, …). Surface
// only the actual stage date ranges.
function timelineStageRows(st) {
  if (!st || typeof st !== 'object') return [];
  return Object.entries(st)
    .filter(([k, v]) => !k.startsWith('_') && v && typeof v === 'object' && !Array.isArray(v)
      && ('startDate' in v || 'targetDate' in v))
    .map(([key, v]) => ({ key, label: prettyStageKey(key), start: v.startDate || null, target: v.targetDate || null }))
    .filter(r => r.start || r.target);
}

// Sort key accessor — pure (row + column key → comparable value).
function sortVal(r, key) {
  switch (key) {
    case 'name': return (r.name || '').toLowerCase();
    case 'code': return (r.code || '').toLowerCase();
    case 'client': return (r.client || '').toLowerCase();
    case 'category': return (r.category || '').toLowerCase();
    case 'status': return STATUS_OPTIONS.indexOf(r.status);
    case 'currency': return r.currency;
    case 'budget': return r.budget;
    case 'targetCB': return r.targetCB;
    case 'finalCB': return r.finalCB;
    case 'projectSize': return r.projectSize ?? -1;
    case 'plotArea': return r.plotArea ?? -1;
    case 'plannedHours': return r.plannedHours ?? -1;
    case 'timeline': return r.timeStart || '9999';
    case 'elevation': return r.elevationM ?? -99999;
    case 'city': return (r.city || 'zzz').toLowerCase();
    case 'country': return (r.country || 'zzz').toLowerCase();
    case 'climate': return r.climate || 'zzz';
    case 'climateTemp': return r.climateNormals?.summerDayC ?? -99999;
    case 'climatePrecip': return (r.climateNormals?.summerPrecipMm ?? 0) + (r.climateNormals?.winterPrecipMm ?? 0);
    case 'soil': return (r.soil || 'zzz').toLowerCase();
    case 'bedrock': return (r.bedrock || 'zzz').toLowerCase();
    case 'comms': return r.comms ? 1 : 0;
    case 'updated': return r.updatedAt || '';
    default: return '';
  }
}

// Site-types filter matcher (multi-value cell against one filter rule).
function matchSiteTypes(types, f) {
  const labels = (types || []).map(siteTypeLabel);
  const val = (f.value ?? '').toString();
  if (f.condition === 'contains') return labels.some(l => l.toLowerCase().includes(val.toLowerCase()));
  if (f.condition === 'is') return labels.includes(val);
  if (f.condition === 'is not') return !labels.includes(val);
  return true;
}

// Soil / Bedrock cells hold more text than their column is wide, so the label
// is clipped to one line and the hover title carries the whole reading. The
// title leads with the cell's own text — otherwise a clipped "Basalt · Late
// Cretaceous" has no way to be read in full — then the detail, then the source
// and its caveat, one per line.
const subsurfaceTitle = (lines) => lines.filter(Boolean).join('\n');

// Reuses the table's shared `ellipsis` rule so these two clip exactly like the
// other overflowing cells (Supervisors, Location) instead of spilling into the
// next column — the table is fixed-layout, so nothing else constrains them.
const subsurfaceCellStyle = (color) => ({ ...ellipsis, fontSize: 10.5, color, cursor: 'help' });

// Detail panel for a Soil/Bedrock reading. A hover `title` is useless on a
// tablet — touch devices fire no hover, so the native tooltip never appears —
// and these readings are exactly what someone reviews on an iPad. So the same
// lines open on tap, positioned at the tap so the panel never lands off-screen
// or gets clipped by the table's own scroll container.
function SubsurfaceInfo({ lines, at, onClose }) {
  useEffect(() => {
    const close = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  const width = 300;
  const left = Math.min(Math.max(8, at.x - width / 2), window.innerWidth - width - 8);
  const top = Math.min(at.y + 12, window.innerHeight - 160);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 600 }} />
      <div style={{
        position: 'fixed', left, top, width, zIndex: 601, background: '#fff',
        border: '1px solid ' + BRIO_BORDER, borderLeft: '2px solid ' + BRIO_GOLD, borderRadius: 4,
        boxShadow: '0 6px 20px rgba(0,0,0,0.16)', padding: '10px 12px', fontFamily: FONT,
        fontSize: 11, lineHeight: 1.5, color: '#334155',
      }}>
        {lines.map((line, i) => (
          <div key={i} style={{
            fontWeight: i === 0 ? 600 : 400,
            color: i === lines.length - 1 ? '#9A988F' : (i === 0 ? '#1e293b' : '#334155'),
            fontSize: i === lines.length - 1 ? 10 : 11,
            marginTop: i === 0 ? 0 : 4,
          }}>{line}</div>
        ))}
      </div>
    </>
  );
}

// One Soil/Bedrock cell: the reading, editable in place. A hand-set value is
// marked with a gold dot so an override is never mistaken for mapped data, and
// carries a revert control — "clear the text" is a real way back to the mapped
// reading, but not one anybody would guess.
function SubsurfaceCell({ lines, text, manual, color, onChange, onRevert }) {
  const [info, setInfo] = useState(null);
  const title = subsurfaceTitle(lines);

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
      {manual && (
        <span title="Set by hand" style={{ width: 5, height: 5, borderRadius: '50%', background: BRIO_GOLD, flexShrink: 0 }} />
      )}
      <span style={{ flex: '1 1 auto', minWidth: 0 }}>
        <EditableCell
          value={text} title={title} placeholder="—"
          displayValue={<span style={subsurfaceCellStyle(manual ? '#1e293b' : color)}>{text || '—'}</span>}
          onChange={onChange}
        />
      </span>
      {manual && (
        <button
          onClick={(e) => { e.stopPropagation(); onRevert(); }}
          title="Restore the mapped reading"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: BRIO_GOLD, flexShrink: 0, display: 'inline-flex' }}
        >
          <RotateCcw size={11} />
        </button>
      )}
      {!!lines.length && (
        <button
          onClick={(e) => { e.stopPropagation(); setInfo({ x: e.clientX, y: e.clientY }); }}
          title={title}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: BRIO_GREY, flexShrink: 0, display: 'inline-flex' }}
        >
          <Info size={11} />
        </button>
      )}
      {info && <SubsurfaceInfo lines={lines} at={info} onClose={() => setInfo(null)} />}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// TOOLBAR ICON
// ══════════════════════════════════════════════════════════════════════════
function ToolbarIcon({ onClick, tooltip, active, badge, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 6px',
        border: '1px solid ' + (active || hovered ? BRIO_GOLD : BRIO_BORDER), borderRadius: 4,
        background: 'transparent', color: active || hovered ? BRIO_GOLD : '#888',
        cursor: 'pointer', transition: 'all 0.15s ease', position: 'relative',
        fontFamily: FONT, fontSize: 10, fontWeight: 600, lineHeight: 1,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      {badge != null && <span style={{ fontSize: 9, fontWeight: 500 }}>({badge})</span>}
      {hovered && tooltip && (
        <span style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: 6, padding: '3px 8px', borderRadius: 3, background: '#333', color: '#fff',
          fontSize: 10, fontWeight: 400, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 300, lineHeight: '14px',
        }}>{tooltip}</span>
      )}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SORT PANEL
// ══════════════════════════════════════════════════════════════════════════
function SortPanel({ sortRules, onChange, onClose, sortableCols }) {
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);
  const used = sortRules.map(r => r.key);
  const available = sortableCols.filter(c => !used.includes(c.key));
  return (
    <div ref={ref} style={panelStyle(320)}>
      <PanelHead title="Sort" onClose={onClose} />
      {sortRules.map((rule, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: BRIO_GREY, minWidth: 28 }}>{idx === 0 ? 'by' : 'then'}</span>
          <select value={rule.key} onChange={e => { const n = [...sortRules]; n[idx] = { ...n[idx], key: e.target.value }; onChange(n); }} style={selStyle(1)}>
            {sortableCols.filter(c => c.key === rule.key || !used.includes(c.key)).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <button onClick={() => { const n = [...sortRules]; n[idx] = { ...n[idx], dir: rule.dir === 'asc' ? 'desc' : 'asc' }; onChange(n); }} style={dirBtnStyle}>
            {rule.dir === 'asc' ? '↑ Asc' : '↓ Desc'}
          </button>
          <button onClick={() => onChange(sortRules.filter((_, i) => i !== idx))} style={xBtnStyle}><X size={12} /></button>
        </div>
      ))}
      {available.length > 0 && (
        <button onClick={() => onChange([...sortRules, { key: available[0].key, dir: 'asc' }])} style={addBtnStyle}>+ Add sort rule</button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// FILTER PANEL
// ══════════════════════════════════════════════════════════════════════════
function FilterPanel({ filterRules, onChange, onClose, uniqueValues, filterableCols }) {
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);
  return (
    <div ref={ref} style={panelStyle(400)}>
      <PanelHead title="Filter" onClose={onClose} />
      {filterRules.map((rule, idx) => {
        const vals = uniqueValues[rule.key] || [];
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <select value={rule.key} onChange={e => { const n = [...filterRules]; n[idx] = { key: e.target.value, condition: 'is', value: '' }; onChange(n); }} style={selStyle(0, 90)}>
              {filterableCols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select value={rule.condition} onChange={e => { const n = [...filterRules]; n[idx] = { ...n[idx], condition: e.target.value }; onChange(n); }} style={selStyle(0)}>
              <option value="is">is</option>
              <option value="is not">is not</option>
              <option value="contains">contains</option>
            </select>
            {rule.condition === 'contains' ? (
              <input type="text" value={rule.value} placeholder="Search…" onChange={e => { const n = [...filterRules]; n[idx] = { ...n[idx], value: e.target.value }; onChange(n); }} style={selStyle(1)} />
            ) : (
              <select value={rule.value} onChange={e => { const n = [...filterRules]; n[idx] = { ...n[idx], value: e.target.value }; onChange(n); }} style={selStyle(1)}>
                <option value="">— Select —</option>
                {vals.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            )}
            <button onClick={() => onChange(filterRules.filter((_, i) => i !== idx))} style={xBtnStyle}><X size={12} /></button>
          </div>
        );
      })}
      {filterableCols.length > 0 && (
        <button onClick={() => onChange([...filterRules, { key: filterableCols[0].key, condition: 'is', value: '' }])} style={addBtnStyle}>+ Add filter rule</button>
      )}
    </div>
  );
}

function PanelHead({ title, onClose }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#333', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: 2 }}><X size={14} /></button>
    </div>
  );
}
const panelStyle = (minW) => ({ position: 'absolute', top: '100%', right: 0, zIndex: 200, background: '#fff', border: '1px solid ' + BRIO_BORDER, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: minW, marginTop: 4, padding: 12, fontFamily: FONT });
const selStyle = (flex, minW) => ({ flex: flex || undefined, minWidth: minW || undefined, fontSize: 11, padding: '4px 6px', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, fontFamily: FONT });
const dirBtnStyle = { background: 'none', border: '1px solid ' + BRIO_BORDER, borderRadius: 3, cursor: 'pointer', padding: '3px 6px', fontSize: 10, color: BRIO_GOLD, fontFamily: FONT };
const xBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', color: BRIO_RED, padding: 2 };
const addBtnStyle = { fontSize: 11, color: BRIO_GOLD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontWeight: 500, padding: '4px 0' };

// ══════════════════════════════════════════════════════════════════════════
// EDITABLE CELL — inline text / number / select
// ══════════════════════════════════════════════════════════════════════════
function EditableCell({ value, onChange, type, options, displayValue, align, placeholder, title }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef(null);
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    if (String(draft) !== String(value ?? '')) onChange(draft);
  };

  if (!editing) {
    return (
      <span
        onClick={() => setEditing(true)}
        title={title ? `${title}\nClick to edit` : 'Click to edit'}
        style={{ cursor: 'pointer', display: 'block', minHeight: 16, borderBottom: '1px dashed ' + BRIO_GOLD + '60', color: (value === '' || value == null) ? BRIO_GREY : '#1e293b', fontSize: 11, lineHeight: '16px', textAlign: align || 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {displayValue != null ? displayValue : (value === '' || value == null ? (placeholder || '—') : String(value))}
      </span>
    );
  }
  if (type === 'select') {
    return (
      <select ref={inputRef} value={draft} onChange={e => setDraft(e.target.value)} onBlur={commit}
        style={{ fontSize: 11, padding: '1px 4px', border: '1px solid ' + BRIO_GOLD, borderRadius: 2, fontFamily: FONT, width: '100%' }}>
        <option value="">—</option>
        {(options || []).map(o => {
          const val = (o && typeof o === 'object') ? o.value : o;
          const lab = (o && typeof o === 'object') ? o.label : o;
          return <option key={val} value={val}>{lab}</option>;
        })}
      </select>
    );
  }
  return (
    <input ref={inputRef} type={type === 'number' || type === 'money' ? 'number' : 'text'} value={draft}
      onChange={e => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
      style={{ fontSize: 11, padding: '1px 4px', border: '1px solid ' + BRIO_GOLD, borderRadius: 2, fontFamily: FONT, width: '100%', textAlign: align || 'left' }}
    />
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CLIMATE CELL — Köppen dropdown (grouped) + coordinate-based suggestion
// ══════════════════════════════════════════════════════════════════════════
function ClimateCell({ value, suggestion, onChange }) {
  const [editing, setEditing] = useState(false);
  const groups = ['A', 'B', 'C', 'D', 'E'];
  const groupName = { A: 'A · Tropical', B: 'B · Arid', C: 'C · Temperate', D: 'D · Continental', E: 'E · Polar / Alpine' };

  if (editing) {
    return (
      <select
        autoFocus
        value={value || ''}
        onBlur={() => setEditing(false)}
        onChange={e => {
          const v = e.target.value;
          setEditing(false);
          if (v === '__suggest__') { if (suggestion) onChange(suggestion.code); }
          else onChange(v);
        }}
        style={{ fontSize: 11, padding: '1px 4px', border: '1px solid ' + BRIO_GOLD, borderRadius: 2, fontFamily: FONT, width: '100%' }}
      >
        <option value="">— None —</option>
        {suggestion && <option value="__suggest__">★ Suggest: {suggestion.code}</option>}
        {groups.map(g => (
          <optgroup key={g} label={groupName[g]}>
            {KOPPEN_ZONES.filter(z => z.group === g).map(z => <option key={z.code} value={z.code}>{z.label}</option>)}
          </optgroup>
        ))}
      </select>
    );
  }

  const title = value
    ? koppenLabel(value)
    : (suggestion ? `Suggested from coordinates: ${koppenLabel(suggestion.code)} — ${suggestion.basis}. Click to confirm.` : 'Click to set climate');
  return (
    <span onClick={() => setEditing(true)} title={title}
      style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, borderBottom: '1px dashed ' + BRIO_GOLD + '60', fontSize: 11, lineHeight: '16px', color: value ? '#1e293b' : BRIO_GREY }}>
      {value ? (
        <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 2, background: BRIO_GOLD + '18', color: BRIO_GOLD, fontWeight: 600, fontSize: 10 }}>{value}</span>
      ) : suggestion ? (
        <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>{suggestion.code}?</span>
      ) : '—'}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MULTI-SELECT CELL — chips + checkbox popover (fixed-positioned), reused for
// both site types and supervisors. `options` = [{ value, label }], `selected` =
// array of values (string or number). Values compare by strict equality.
// ══════════════════════════════════════════════════════════════════════════
function MultiSelectCell({ selected, options, onChange, emptyLabel = '—', minWidth = 195, placeholder }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const sel = Array.isArray(selected) ? selected : [];

  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    // Close when the page/table behind the panel scrolls — but NOT when the
    // panel's own list scrolls (a capture-phase listener sees those too).
    const onScroll = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const openPanel = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setAnchor({ left: r.left, top: r.top, bottom: r.bottom });
    setOpen(true);
  };
  const toggle = (v) => onChange(sel.includes(v) ? sel.filter(x => x !== v) : [...sel, v]);
  const labelFor = (v) => { const o = options.find(op => op.value === v); return o ? o.label : String(v); };

  // Open downward by default; flip up only when there's clearly more room above.
  // Bound the height to the available space so the whole list scrolls internally.
  const spaceBelow = anchor ? window.innerHeight - anchor.bottom - 8 : 0;
  const spaceAbove = anchor ? anchor.top - 8 : 0;
  const openUp = anchor ? (spaceBelow < 200 && spaceAbove > spaceBelow) : false;
  const maxH = anchor ? Math.max(140, Math.min(340, openUp ? spaceAbove : spaceBelow)) : 320;
  const panelPos = anchor ? {
    position: 'fixed', zIndex: 10002,
    left: Math.max(4, Math.min(anchor.left, window.innerWidth - (minWidth + 16))),
    maxHeight: maxH, overflowY: 'auto',
    ...(openUp ? { bottom: window.innerHeight - anchor.top + 2 } : { top: anchor.bottom + 2 }),
  } : {};

  return (
    <>
      <div ref={triggerRef} onClick={openPanel} title="Click to edit"
        style={{ cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: 2, minHeight: 16, borderBottom: '1px dashed ' + BRIO_GOLD + '60' }}>
        {sel.length === 0
          ? <span style={{ color: BRIO_GREY, fontSize: 11 }}>{placeholder || emptyLabel}</span>
          : sel.map(v => (
            <span key={v} style={{ padding: '1px 5px', background: 'rgba(159,144,42,0.12)', color: BRIO_GOLD, fontSize: 9, fontWeight: 500, borderRadius: 2, lineHeight: '15px', whiteSpace: 'nowrap' }}>{labelFor(v)}</span>
          ))}
      </div>
      {open && (
        <div ref={panelRef} style={{ ...panelPos, background: '#fff', border: '1px solid ' + BRIO_GOLD, borderRadius: 4, boxShadow: '0 6px 18px rgba(0,0,0,0.18)', padding: 6, minWidth }}>
          {options.length === 0 && <div style={{ fontSize: 11, color: BRIO_GREY, padding: '3px 4px' }}>No options</div>}
          {options.map(o => {
            const on = sel.includes(o.value);
            return (
              <button key={o.value} onClick={() => toggle(o.value)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 4px', fontFamily: FONT, fontSize: 11, color: '#1e293b', borderRadius: 3 }}
                onMouseEnter={e => e.currentTarget.style.background = BRIO_BG}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <span style={{ width: 13, height: 13, border: '1px solid ' + (on ? BRIO_GOLD : BRIO_BORDER), borderRadius: 2, background: on ? BRIO_GOLD : '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {on && <Check size={10} color="#fff" />}
                </span>
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// PROJECT DETAIL CARD — right-side panel. Retainer + fee scopes are editable
// (admins only); fee allocation + stage timeline are read-only because they are
// owned by the Stages / Timeline pages that drive invoicing.
// ══════════════════════════════════════════════════════════════════════════
const cardInput = { fontSize: 12, height: 30, padding: '4px 8px', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, fontFamily: FONT, width: '100%', boxSizing: 'border-box', display: 'block', background: '#fff', color: '#334155' };
// Field label sits above its input on its own line, so every control (incl. the
// native date picker) gets the full column width and a consistent height.
const cardFieldLabel = { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase', color: BRIO_GREY, display: 'flex', flexDirection: 'column', gap: 3 };

function ProjectDetailCard({ row, onClose, onSaveFee, canEditFees }) {
  const ref = useRef(null);
  const p = row.p;
  const currency = row.currency;
  const sym = CURRENCY_SYMBOL[currency] || '';
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const origRetainer = p.retainerConfig || {};
  const [monthlyAmount, setMonthlyAmount] = useState(origRetainer.monthlyAmount ?? 0);
  const [totalMonths, setTotalMonths] = useState(origRetainer.totalMonths ?? 12);
  const [status, setStatus] = useState(origRetainer.status || 'inactive');
  const [startDate, setStartDate] = useState(origRetainer.startDate || '');
  const [scopes, setScopes] = useState(() => (Array.isArray(p.miscFeeScopes) ? p.miscFeeScopes.map(s => ({ ...s })) : []));
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const addScope = () => setScopes(s => [...s, { id: Date.now().toString(36) + Math.random().toString(36).slice(2, 7), name: '', amount: 0 }]);
  const updateScope = (idx, patch) => setScopes(s => s.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const removeScope = (idx) => setScopes(s => s.filter((_, i) => i !== idx));

  const retainerActiveTotal = status === 'active' ? (Number(monthlyAmount) || 0) * (parseInt(totalMonths) || 12) : 0;
  const scopesTotal = scopes.reduce((s, x) => s + (Number(x.amount) || 0), 0);

  const handleSave = async () => {
    setSaving(true); setSaveErr('');
    // Preserve every other retainer field (e.g. paidMonths, used by retainer
    // tracking) and write the whole object, exactly like the edit modal.
    const retainerConfig = {
      ...origRetainer,
      monthlyAmount: Number(monthlyAmount) || 0,
      totalMonths: parseInt(totalMonths) || 12,
      status: status || 'inactive',
      startDate: startDate || null,
    };
    const miscFeeScopes = scopes
      .filter(s => s.name && s.name.trim())
      .map(s => ({ id: s.id, name: s.name.trim(), amount: Number(s.amount) || 0 }));
    const res = await onSaveFee(row, { retainerConfig, miscFeeScopes });
    setSaving(false);
    if (!res?.success) setSaveErr(res?.error || 'Could not save.');
    else onClose();
  };

  const section = (title, body, extra) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 5 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: BRIO_GREY }}>{title}</div>
        {extra}
      </div>
      {body}
    </div>
  );
  const note = (t) => <div style={{ fontSize: 10.5, color: '#9A988F', fontStyle: 'italic', marginTop: 4 }}>{t}</div>;
  const emptyBlock = (t) => <div style={{ fontSize: 11.5, color: '#9A988F', background: BRIO_BG, border: '1px solid ' + BRIO_BORDER, borderRadius: 4, padding: '8px 10px' }}>{t}</div>;
  const roThStyle = { textAlign: 'left', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9A988F', padding: '4px 8px', borderBottom: '1px solid ' + BRIO_BORDER, background: BRIO_BG };
  const roTdStyle = { fontSize: 11.5, color: '#334155', padding: '4px 8px', borderBottom: '1px solid ' + BRIO_BORDER, whiteSpace: 'nowrap' };

  // Fee allocation → Stage | % table (values are per-stage percentages).
  const allocationBlock = (ba) => {
    const rows = allocationRows(ba);
    if (!rows.length) return emptyBlock('Not allocated yet — set on the Stages page.');
    const total = rows.reduce((s, r) => s + r.pct, 0);
    return (
      <div style={{ border: '1px solid ' + BRIO_BORDER, borderRadius: 4, overflow: 'hidden', maxHeight: 240, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
          <thead><tr><th style={roThStyle}>Stage</th><th style={{ ...roThStyle, textAlign: 'right' }}>Allocation</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key}><td style={roTdStyle}>{r.label}</td><td style={{ ...roTdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.pct}%</td></tr>
            ))}
            <tr><td style={{ ...roTdStyle, fontWeight: 700, borderBottom: 'none' }}>Total</td><td style={{ ...roTdStyle, textAlign: 'right', fontWeight: 700, borderBottom: 'none', fontVariantNumeric: 'tabular-nums' }}>{Math.round(total * 10) / 10}%</td></tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Stage timeline → Stage | Start | Target table (real stage date ranges only).
  const timelineBlock = (st) => {
    const rows = timelineStageRows(st);
    if (!rows.length) return emptyBlock('No stage dates yet — set on the Timeline page.');
    return (
      <div style={{ border: '1px solid ' + BRIO_BORDER, borderRadius: 4, overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
          <thead><tr><th style={roThStyle}>Stage</th><th style={roThStyle}>Start</th><th style={roThStyle}>Target</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key}>
                <td style={roTdStyle}>{r.label}</td>
                <td style={{ ...roTdStyle, color: r.start ? '#334155' : '#c4c2ba' }}>{r.start ? fmtDate(r.start) : '—'}</td>
                <td style={{ ...roTdStyle, color: r.target ? '#334155' : '#c4c2ba' }}>{r.target ? fmtDate(r.target) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div ref={ref} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '94vw', background: '#fff', borderLeft: '2px solid ' + BRIO_GOLD, boxShadow: '-8px 0 24px rgba(0,0,0,0.12)', zIndex: 500, overflowY: 'auto', fontFamily: FONT, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: BRIO_GOLD }}>{row.name}</div>
          <div style={{ fontSize: 11, color: BRIO_GREY }}>{row.code || '—'} · {row.client}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}><X size={16} /></button>
      </div>

      {section('Site', (
        <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6 }}>
          <div>Location: {row.address || '—'}</div>
          <div>Elevation: {row.elevationM != null ? `${Math.round(row.elevationM)} m` : '—'}{p.elevationSource ? ` (${p.elevationSource})` : ''}</div>
          <div>Climate: {row.climate ? koppenLabel(row.climate) : '—'}</div>
          <div>Site type: {(row.siteTypes || []).map(siteTypeLabel).join(', ') || '—'}</div>
          {(row.subsurface || row.subsurfaceOverride) && (() => {
            const soil = row.subsurface?.soil;
            const bedrock = row.subsurface?.bedrock;
            const soilRes = resolveSoil(row.subsurface, row.subsurfaceOverride);
            const rockRes = resolveBedrock(row.subsurface, row.subsurfaceOverride);
            // A hand-set reading leads, with the mapped one kept underneath it —
            // the override replaces what is shown, never what was measured.
            const byHand = <span style={{ color: BRIO_GOLD, fontSize: 10.5 }}> (set by hand)</span>;
            return (
              <>
                {soilRes.text && <div>Soil: {soilRes.text}{soilRes.manual && byHand}</div>}
                {soil && (
                  <>
                    <div style={{ color: soilRes.manual ? '#9A988F' : undefined }}>Mapped soil {describeSoilSlice(soil.surface) || '—'}</div>
                    {soil.deep && <div style={{ color: soilRes.manual ? '#9A988F' : undefined }}>Mapped soil {describeSoilSlice(soil.deep)}</div>}
                  </>
                )}
                {rockRes.text && <div>Bedrock: {rockRes.text}{rockRes.manual && byHand}</div>}
                {bedrock && rockRes.manual && (
                  <div style={{ color: '#9A988F' }}>Mapped bedrock: {resolveBedrock(row.subsurface, null).text}</div>
                )}
                {bedrock?.unit && <div style={{ fontSize: 10.5, color: '#9A988F' }}>{bedrock.unit}</div>}
                <div style={{ fontSize: 10.5, color: '#9A988F' }}>
                  Regional context only — {soil ? `soil ${soil.source}, 250 m grid` : 'soil unavailable'}
                  {bedrock ? `, bedrock ${bedrock.source}` : ''}
                  {row.subsurface?.updatedAt ? ` · ${row.subsurface.updatedAt}` : ''}. Not a site investigation.
                  {' '}Edit Soil or Bedrock in the table to override it; the ↺ beside a hand-set value restores the mapped reading.
                </div>
              </>
            );
          })()}
          {row.climateNormals && (() => {
            const cn = row.climateNormals;
            const t = (d, n) => `${d != null ? Math.round(d) : '–'}° / ${n != null ? Math.round(n) : '–'}°`;
            return (
              <>
                <div>Winter (day/night): {t(cn.winterDayC, cn.winterNightC)}{cn.winterPrecipMm != null ? ` · ${cn.winterPrecipMm} mm` : ''}</div>
                <div>Summer (day/night): {t(cn.summerDayC, cn.summerNightC)}{cn.summerPrecipMm != null ? ` · ${cn.summerPrecipMm} mm` : ''}</div>
                <div style={{ fontSize: 10.5, color: '#9A988F' }}>Climate normals{cn.period ? ` · ${cn.period}` : ''}{cn.source ? ` · ${cn.source}` : ''}</div>
              </>
            );
          })()}
        </div>
      ))}
      {section('Supervisors', <div style={{ fontSize: 12, color: '#334155' }}>{row.supervisorNames.length ? row.supervisorNames.join(', ') : '—'}</div>)}

      {canEditFees && (
        <>
          {section('Retainer', (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={cardFieldLabel}>Monthly ({sym || currency})
                <input type="number" autoComplete="off" value={monthlyAmount} onChange={e => setMonthlyAmount(e.target.value)} style={cardInput} />
              </label>
              <label style={cardFieldLabel}>Total months
                <input type="number" autoComplete="off" value={totalMonths} onChange={e => setTotalMonths(e.target.value)} style={cardInput} />
              </label>
              <label style={cardFieldLabel}>Status
                <select value={status} onChange={e => setStatus(e.target.value)} style={cardInput}>
                  <option value="inactive">inactive</option>
                  <option value="active">active</option>
                </select>
              </label>
              <label style={cardFieldLabel}>Start date
                <input type="date" value={startDate || ''} onChange={e => setStartDate(e.target.value)} style={cardInput} />
              </label>
              <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#334155' }}>
                Retainer fee: <b>{status === 'active' ? fmtMoney(retainerActiveTotal, currency) : '—'}</b>
                {origRetainer.paidMonths != null && <span style={{ color: BRIO_GREY }}> · {origRetainer.paidMonths} paid month(s) preserved</span>}
              </div>
            </div>
          ))}

          {section(`Fee scopes (${scopes.length})`, (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {scopes.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#9A988F' }}>
                  <span style={{ flex: 1 }}>Scope</span>
                  <span style={{ width: 120 }}>Amount ({sym || currency})</span>
                  <span style={{ width: 20 }} />
                </div>
              )}
              {scopes.map((s, idx) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="text" autoComplete="off" placeholder="e.g. Landscape design" value={s.name} onChange={e => updateScope(idx, { name: e.target.value })} style={{ ...cardInput, flex: 1 }} />
                  <input type="number" autoComplete="off" placeholder="0" value={s.amount} onChange={e => updateScope(idx, { amount: e.target.value })} style={{ ...cardInput, width: 120, textAlign: 'right' }} />
                  <button onClick={() => removeScope(idx)} title="Remove scope" style={{ flexShrink: 0, width: 20, background: 'none', border: 'none', cursor: 'pointer', color: BRIO_RED, padding: 0, display: 'inline-flex', justifyContent: 'center' }}><Trash2 size={13} /></button>
                </div>
              ))}
              <button onClick={addScope} style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: '1px dashed ' + BRIO_BORDER, cursor: 'pointer', padding: '5px 10px', fontSize: 11, color: BRIO_GOLD, fontFamily: FONT, borderRadius: 4, marginTop: 2 }}>
                <Plus size={12} /> Add scope
              </button>
              <div style={{ fontSize: 11, color: '#334155', marginTop: 2 }}>Fee scopes total: <b>{fmtMoney(scopesTotal, currency)}</b></div>
            </div>
          ))}

          {note('Changing the fee, retainer, or fee scopes records a fee revision (as the edit modal does), so the Invoices page stays in sync.')}
          {saveErr && <div style={{ fontSize: 11, color: BRIO_RED, marginTop: 8 }}>{saveErr}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, marginBottom: 18 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 4, cursor: saving ? 'default' : 'pointer', border: 'none', background: BRIO_GOLD, color: '#fff', fontFamily: FONT, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Saving…' : 'Save retainer & fee scopes'}
            </button>
            <button onClick={onClose} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 4, cursor: 'pointer', border: '1px solid ' + BRIO_BORDER, background: '#fff', color: BRIO_GREY, fontFamily: FONT }}>Cancel</button>
          </div>
        </>
      )}

      {section('Fee allocation (per stage)', allocationBlock(p.budgetAllocation),
        <span style={{ fontSize: 9, color: '#9A988F', textTransform: 'uppercase', letterSpacing: '0.06em' }}>read-only</span>)}
      {note('Edited on the Stages page — it splits the fee across stages and drives per-stage invoice amounts.')}
      <div style={{ height: 12 }} />
      {section('Stage timeline', timelineBlock(p.stageTimeline),
        <span style={{ fontSize: 9, color: '#9A988F', textTransform: 'uppercase', letterSpacing: '0.06em' }}>read-only</span>)}
      {note('Edited on the Timeline page — stage dates feed invoice scheduling.')}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════
export default function ProjectsOverviewPage({
  activeProjects = [],
  activeClients = [],
  activeUsers = [],
  BRIO_PROJECT_TYPES = [],
  getProjectStatusStyle,
  updateProject,
  currentUser,
  isAdmin = false,
  feeRevisionsHook = null,
  canManageProject = null,
}) {
  // Who may open a project's detail card: admins always; a supervisor only for
  // projects they supervise (direct assignment or via their team). Falls back to
  // permissive when the gate isn't supplied (e.g. local dev without Supabase).
  const canOpenDetail = useCallback(
    (row) => (isAdmin || !canManageProject ? true : !!canManageProject(row.id)),
    [isAdmin, canManageProject]
  );
  const [sortRules, setSortRules] = useState([{ key: 'name', dir: 'asc' }]);
  const [filterRules, setFilterRules] = useState([]);
  const [showSort, setShowSort] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [search, setSearch] = useState('');
  const [detailRow, setDetailRow] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [fetchState, setFetchState] = useState({ running: false, done: 0, total: 0 });
  const [climateFetch, setClimateFetch] = useState({ running: false, done: 0, total: 0 });
  const [subsurfaceFetch, setSubsurfaceFetch] = useState({ running: false, done: 0, total: 0 });

  // ── Role-visible columns (fee/currency are admin-only) ───────────────────
  const baseColumns = useMemo(() => COLUMNS.filter(c => isAdmin || !c.hideForSupervisor), [isAdmin]);
  const sortableCols = useMemo(() => baseColumns.filter(c => c.sortable), [baseColumns]);
  const filterableCols = useMemo(() => baseColumns.filter(c => c.filterable), [baseColumns]);

  // ── Column layout state (order / frozen / collapsed), persisted ──────────
  const [frozenCols, setFrozenCols] = useState(() => loadFrozenCols());
  const [collapsedCols, setCollapsedCols] = useState(() => loadCollapsedCols());
  const [columnOrder, setColumnOrder] = useState(() => loadStoredColumnOrder());
  const [colWidths, setColWidths] = useState(() => loadColWidths());
  const orderedColumns = useMemo(() => getOrderedColumns(columnOrder, frozenCols, baseColumns), [columnOrder, frozenCols, baseColumns]);

  // ── Column width resize (drag the right edge of a header) ────────────────
  const resizeRef = useRef(null); // { key, startX, startWidth, minW } while dragging
  const persistColWidths = useCallback((next) => {
    try { localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(next)); } catch {}
  }, []);
  const startColResize = useCallback((e, col) => {
    // Left button only; never let this begin a header reorder-drag or a sort.
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { key: col.key, startX: e.clientX, startWidth: colWidthOf(col, colWidths), minW: col.minW };
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    const onMove = (ev) => {
      const r = resizeRef.current;
      if (!r) return;
      const next = Math.max(r.minW, Math.round(r.startWidth + (ev.clientX - r.startX)));
      setColWidths(prev => ({ ...prev, [r.key]: next }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = '';
      resizeRef.current = null;
      setColWidths(prev => { persistColWidths(prev); return prev; });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [colWidths, persistColWidths]);
  // Double-click the handle → reset that column to its default width.
  const resetColWidth = useCallback((key) => setColWidths(prev => {
    if (!(key in prev)) return prev;
    const next = { ...prev }; delete next[key];
    persistColWidths(next);
    return next;
  }), [persistColWidths]);

  const toggleFreezeCol = useCallback((key) => setFrozenCols(prev => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem(FROZEN_COLS_KEY, JSON.stringify(next)); } catch {}
    return next;
  }), []);
  const toggleColCollapse = useCallback((key) => setCollapsedCols(prev => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next)); } catch {}
    return next;
  }), []);
  const moveColumn = useCallback((fromIdx, toIdx) => {
    setColumnOrder(prev => {
      const currentKeys = getOrderedColumns(prev, frozenCols, baseColumns).map(c => c.key);
      const firstNonFrozen = currentKeys.findIndex(k => {
        const col = baseColumns.find(c => c.key === k);
        return !col?.alwaysFrozen && !frozenCols[k];
      });
      if (toIdx < firstNonFrozen && toIdx < fromIdx) return prev;
      const next = [...currentKeys];
      const [removed] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, removed);
      try { localStorage.setItem(COL_ORDER_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [frozenCols, baseColumns]);

  // Drag + touch reorder state
  const dragColIdxRef = useRef(null);
  const [dragOverColIdx, setDragOverColIdx] = useState(null);
  const touchStartColRef = useRef(null);
  const touchCurrentColRef = useRef(null);
  const handleTouchStart = useCallback((colIdx, col) => {
    if (col.alwaysFrozen || frozenCols[col.key]) return;
    touchStartColRef.current = colIdx;
    touchCurrentColRef.current = colIdx;
    dragColIdxRef.current = colIdx;
  }, [frozenCols]);
  const handleTouchMove = useCallback((e) => {
    if (touchStartColRef.current == null) return;
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const th = el && el.closest('th');
    if (th && th.dataset.colIdx != null) {
      const idx = Number(th.dataset.colIdx);
      touchCurrentColRef.current = idx;
      setDragOverColIdx(idx);
    }
  }, []);
  const handleTouchEnd = useCallback(() => {
    const from = touchStartColRef.current;
    const to = touchCurrentColRef.current;
    if (from != null && to != null && from !== to) moveColumn(from, to);
    touchStartColRef.current = null;
    touchCurrentColRef.current = null;
    dragColIdxRef.current = null;
    setDragOverColIdx(null);
  }, [moveColumn]);

  // Header click → quick sort (asc → desc → off), single-key.
  const handleColumnSort = useCallback((key) => {
    const col = baseColumns.find(c => c.key === key);
    if (!col?.sortable) return;
    setSortRules(prev => {
      const existing = prev.find(r => r.key === key);
      if (existing) {
        if (existing.dir === 'asc') return prev.map(r => r.key === key ? { ...r, dir: 'desc' } : r);
        return prev.filter(r => r.key !== key);
      }
      return [{ key, dir: 'asc' }];
    });
  }, [baseColumns]);

  // ── Lookup maps + option lists ───────────────────────────────────────────
  const clientName = useMemo(() => {
    const m = new Map();
    activeClients.forEach(c => m.set(c.id, c.organisation || c.name || `#${c.id}`));
    return m;
  }, [activeClients]);
  const userName = useMemo(() => {
    const m = new Map();
    activeUsers.forEach(u => m.set(Number(u.id), u.name || `#${u.id}`));
    return m;
  }, [activeUsers]);
  const clientOptions = useMemo(() => activeClients
    .map(c => ({ value: c.id, label: c.organisation || c.name || `#${c.id}` }))
    .sort((a, b) => a.label.localeCompare(b.label)), [activeClients]);
  // Supervisors picker: only active users who are supervisors or admins.
  const supervisorOptions = useMemo(() => activeUsers
    .filter(u => u.isActive !== false && SUPERVISOR_ROLES.includes(u.role))
    .map(u => ({ value: Number(u.id), label: u.name || `#${u.id}` }))
    .sort((a, b) => a.label.localeCompare(b.label)), [activeUsers]);
  const eligibleSupervisorIds = useMemo(() => new Set(supervisorOptions.map(o => o.value)), [supervisorOptions]);

  // ── Saves ─────────────────────────────────────────────────────────────────
  const save = useCallback(async (id, patch) => {
    setError('');
    const res = await updateProject(id, patch);
    if (!res?.success) setError(res?.error || 'Could not save the change.');
    return res;
  }, [updateProject]);

  // Fee-affecting save: writes the field(s), then records a fee revision when the
  // total fee changed — the same formula/path the edit modal uses, so the
  // Invoices page and fee history don't drift.
  const saveWithFeeRevision = useCallback(async (row, updates) => {
    const oldTotalFee = calculateTotalFee(row.p);
    const res = await save(row.id, updates);
    if (!res?.success) return res;
    const newTotalFee = calculateTotalFee({ ...row.p, ...updates });
    if (oldTotalFee !== newTotalFee && feeRevisionsHook?.createRevision && SUPERVISOR_ROLES.includes(currentUser?.role)) {
      try {
        await feeRevisionsHook.createRevision({
          projectId: row.id,
          effectiveDate: new Date().toISOString().slice(0, 10),
          oldTotalFee,
          newTotalFee,
          reason: 'Fee updated in projects overview',
          currency: row.p.contractCurrency || 'INR',
        });
      } catch (e) {
        console.error('[ProjectsOverview] fee revision failed:', e);
      }
    }
    return res;
  }, [save, feeRevisionsHook, currentUser]);

  // ── Rows (derived) ───────────────────────────────────────────────────────
  const rows = useMemo(() => activeProjects.map(p => {
    const coords = parseCoordinates(p.projectAddress || '');
    const tdates = collectTimelineDates(p.stageTimeline).sort();
    const supervisorNames = (p.supervisors || []).map(id => userName.get(Number(id))).filter(Boolean);
    const suggestion = coords ? suggestKoppen(coords.lat, coords.lng, p.elevationM) : null;
    return {
      id: p.id,
      p,
      name: p.name || '—',
      code: p.projectCode || '',
      color: p.color,
      client: clientName.get(p.clientId) || '—',
      category: p.category || '',
      status: p.status || 'active',
      supervisorNames,
      supervisorIds: (p.supervisors || []).map(Number),
      currency: p.contractCurrency || 'INR',
      budget: Number(p.budget) || 0,
      targetCB: Number(p.targetConstructionBudget) || 0,
      finalCB: Number(p.finalConstructionBudget) || 0,
      projectSize: p.projectSize != null ? Number(p.projectSize) : null,
      plotArea: p.plotArea != null ? Number(p.plotArea) : null,
      plannedHours: p.plannedHours != null ? Number(p.plannedHours) : null,
      timeStart: tdates[0] || null,
      timeEnd: tdates[tdates.length - 1] || null,
      address: p.projectAddress || '',
      city: p.city || '',
      country: p.country || '',
      coords,
      elevationM: p.elevationM != null ? Number(p.elevationM) : null,
      climate: p.climateZone || '',
      suggestion,
      siteTypes: Array.isArray(p.siteTypes) ? p.siteTypes : [],
      climateNormals: (p.climateNormals && typeof p.climateNormals === 'object') ? p.climateNormals : null,
      // The raw blob drives the detail card and tooltips; the two derived
      // strings are what the table sorts, filters and searches on.
      subsurface: (p.subsurface && typeof p.subsurface === 'object') ? p.subsurface : null,
      // A human override wins over the mapped reading; `manual` marks the cell
      // and keeps the mapped value available in the tooltip.
      subsurfaceOverride: (p.subsurfaceOverride && typeof p.subsurfaceOverride === 'object') ? p.subsurfaceOverride : null,
      soil: resolveSoil(p.subsurface, p.subsurfaceOverride).text || '',
      soilManual: resolveSoil(p.subsurface, p.subsurfaceOverride).manual,
      bedrock: resolveBedrock(p.subsurface, p.subsurfaceOverride).text || '',
      bedrockManual: resolveBedrock(p.subsurface, p.subsurfaceOverride).manual,
      invoiceName: p.invoiceProjectName || '',
      email: p.projectEmailId || '',
      comms: !!p.commsEnabled,
      updatedAt: p.updatedAt || null,
    };
  }), [activeProjects, clientName, userName]);

  // Keep the open detail card in sync with the latest row data after a save.
  const detailRowLive = useMemo(
    () => (detailRow ? rows.find(r => r.id === detailRow.id) || null : null),
    [detailRow, rows]
  );

  // ── Unique values for filter dropdowns ───────────────────────────────────
  const uniqueValues = useMemo(() => {
    const uv = {};
    const add = (k, v) => { if (v === '' || v == null) return; (uv[k] = uv[k] || new Set()).add(v); };
    rows.forEach(r => {
      add('code', r.code); add('client', r.client); add('category', r.category);
      add('status', r.status); add('currency', r.currency); add('climate', r.climate);
      add('city', r.city); add('country', r.country);
      add('comms', r.comms ? 'Yes' : 'No');
      add('soil', r.soil); add('bedrock', r.bedrock);
      (r.siteTypes || []).forEach(t => add('siteTypes', siteTypeLabel(t)));
    });
    const out = {};
    Object.entries(uv).forEach(([k, set]) => { out[k] = [...set].sort(); });
    return out;
  }, [rows]);

  // ── Filter → search → sort ───────────────────────────────────────────────
  const displayRows = useMemo(() => {
    let out = rows;
    filterRules.filter(f => f.value !== '' || f.condition === 'contains').forEach(f => {
      out = out.filter(r => {
        let cell;
        if (f.key === 'comms') cell = r.comms ? 'Yes' : 'No';
        else if (f.key === 'siteTypes') return matchSiteTypes(r.siteTypes, f);
        else cell = r[f.key];
        cell = (cell ?? '').toString();
        const val = (f.value ?? '').toString();
        if (f.condition === 'is') return cell === val;
        if (f.condition === 'is not') return cell !== val;
        if (f.condition === 'contains') return cell.toLowerCase().includes(val.toLowerCase());
        return true;
      });
    });
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(r => [r.name, r.code, r.client, r.category, r.address, r.city, r.country, r.climate, r.soil, r.bedrock, r.invoiceName, r.email, ...(r.siteTypes || []).map(siteTypeLabel), ...r.supervisorNames]
        .filter(Boolean).some(v => v.toString().toLowerCase().includes(q)));
    }
    const rules = sortRules.length ? sortRules : [{ key: 'name', dir: 'asc' }];
    out = [...out].sort((a, b) => {
      for (const rule of rules) {
        const av = sortVal(a, rule.key), bv = sortVal(b, rule.key);
        let cmp = 0;
        if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
        else cmp = String(av).localeCompare(String(bv));
        if (cmp !== 0) return rule.dir === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
    return out;
  }, [rows, filterRules, search, sortRules]);

  // ── Elevation candidates & bulk fetch ────────────────────────────────────
  const elevationCandidates = useMemo(
    () => rows.filter(r => r.coords && (r.elevationM == null || r.p.elevationSource === 'google' || r.p.elevationSource === 'open-meteo')),
    [rows]
  );

  const runElevationFetch = useCallback(async (candidates) => {
    if (!candidates.length) return;
    setError('');
    setFetchState({ running: true, done: 0, total: candidates.length });
    try {
      const elevs = await fetchElevations(candidates.map(r => ({ id: r.id, lat: r.coords.lat, lng: r.coords.lng })));
      const updates = candidates.filter(r => elevs.has(r.id)).map(r => ({ id: r.id, m: Math.round(elevs.get(r.id) * 10) / 10 }));
      let done = 0;
      const queue = [...updates];
      const worker = async () => {
        while (queue.length) {
          const u = queue.shift();
          // eslint-disable-next-line no-await-in-loop
          await save(u.id, { elevationM: u.m, elevationSource: 'open-meteo' });
          done += 1;
          setFetchState({ running: true, done, total: candidates.length });
        }
      };
      await Promise.all(Array.from({ length: Math.min(6, updates.length) }, worker));
      setFetchState({ running: false, done, total: candidates.length });
      if (!updates.length) setError('No elevation returned for these coordinates.');
    } catch (e) {
      setFetchState({ running: false, done: 0, total: 0 });
      setError(e?.message || 'Elevation lookup failed.');
    }
  }, [save]);

  // ── Climate normals: candidates & fetch (Open-Meteo archive) ──────────────
  const climateCandidates = useMemo(
    () => rows.filter(r => r.coords && !r.climateNormals),
    [rows]
  );

  const runClimateFetch = useCallback(async (candidates) => {
    if (!candidates.length) return;
    setError(''); setNotice('');
    setClimateFetch({ running: true, done: 0, total: candidates.length });
    try {
      const { normals: normalsById, failures } = await fetchClimateNormals(
        candidates.map(r => ({ id: r.id, lat: r.coords.lat, lng: r.coords.lng })));
      const updates = candidates.filter(r => normalsById.has(r.id)).map(r => ({ id: r.id, normals: normalsById.get(r.id) }));
      let done = 0;
      const queue = [...updates];
      const worker = async () => {
        while (queue.length) {
          const u = queue.shift();
          // eslint-disable-next-line no-await-in-loop
          await save(u.id, { climateNormals: u.normals });
          done += 1;
          setClimateFetch({ running: true, done, total: candidates.length });
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, updates.length) }, worker));
      setClimateFetch({ running: false, done, total: candidates.length });

      // Distinguish rate-limiting (transient, retryable) from a genuine no-data
      // cell, so the message tells the user what to do next.
      const rateLimited = failures.filter(f => /rate-limited|HTTP 5|network/.test(f.reason)).length;
      if (!updates.length) {
        setError(rateLimited
          ? 'The weather service is rate-limiting the request. Wait a minute, then click Climate again.'
          : 'No climate data returned for these coordinates.');
      } else if (failures.length) {
        setNotice(rateLimited
          ? `Fetched ${updates.length} site${updates.length === 1 ? '' : 's'}; ${failures.length} still pending (weather service rate-limited). Click Climate again in a moment to finish.`
          : `Fetched ${updates.length} site${updates.length === 1 ? '' : 's'}; ${failures.length} returned no data for their location.`);
      } else {
        setNotice(`Fetched climate normals for ${updates.length} site${updates.length === 1 ? '' : 's'}.`);
      }
    } catch (e) {
      setClimateFetch({ running: false, done: 0, total: 0 });
      setError(e?.message || 'Climate lookup failed.');
    }
  }, [save]);

  // ── Subsurface: candidates & fetch (SoilGrids + Macrostrat) ───────────────
  // A site with either half already filled is not re-fetched; clearing it in
  // the detail card is how you ask for a refresh.
  const subsurfaceCandidates = useMemo(
    () => rows.filter(r => r.coords && !r.subsurface),
    [rows]
  );

  const runSubsurfaceFetch = useCallback(async (candidates) => {
    if (!candidates.length) return;
    setError(''); setNotice('');
    setSubsurfaceFetch({ running: true, done: 0, total: candidates.length });
    try {
      // Save and count each site as it settles rather than batching at the end:
      // these services are rate-limited, so a portfolio-wide run takes minutes,
      // and a run that shows no progress reads as frozen — and would lose
      // everything if the tab were closed partway.
      let done = 0;
      let saved = 0;
      const { failures } = await fetchSubsurface(
        candidates.map(r => ({ id: r.id, lat: r.coords.lat, lng: r.coords.lng })),
        {
          onResult: async ({ id, subsurface }) => {
            if (subsurface) {
              await save(id, { subsurface });
              saved += 1;
            }
            done += 1;
            setSubsurfaceFetch({ running: true, done, total: candidates.length });
          },
        });
      setSubsurfaceFetch({ running: false, done, total: candidates.length });

      // A service being down or throttling is worth retrying; "nothing mapped
      // here" is not, so the message distinguishes them the way climate does.
      const unavailable = failures.filter(f => /unavailable|timed out|HTTP 5|network/.test(f.reason)).length;
      const sites = (n) => `${n} site${n === 1 ? '' : 's'}`;
      if (!saved) {
        setError(unavailable
          ? 'The soil and geology services are rate-limiting or unavailable right now. Try Subsurface again in a few minutes — finished sites are already saved.'
          : 'No soil or geology data returned for these coordinates.');
      } else if (failures.length) {
        setNotice(`Fetched ${sites(saved)}; ${failures.length} returned nothing${unavailable ? ' (service rate-limited — click Subsurface again to finish the rest)' : ' for their location'}.`);
      } else {
        setNotice(`Fetched subsurface context for ${sites(saved)}. Regional data — not a site investigation.`);
      }
    } catch (e) {
      setSubsurfaceFetch({ running: false, done: 0, total: 0 });
      setError(e?.message || 'Subsurface lookup failed.');
    }
  }, [save]);

  // Empty Soil / Bedrock cells share one affordance: fetch this site alone.
  const subsurfaceGetCell = (r) => {
    if (!r.coords || subsurfaceFetch.running) return '—';
    return (
      <button title="Fetch regional soil and bedrock for this site" onClick={() => runSubsurfaceFetch([r])}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_GREEN, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontFamily: FONT }}>
        <Layers size={12} /> get
      </button>
    );
  };

  // ── Render one cell's content (the <td> wrapper is added by the body map) ─
  const renderCell = (col, r) => {
    switch (col.key) {
      case 'name': {
        const canOpen = canOpenDetail(r);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
            <input type="color" value={r.color || '#3b82f6'} title="Project colour"
              onClick={e => e.stopPropagation()} onChange={e => save(r.id, { color: e.target.value })}
              style={{ width: 13, height: 13, padding: 0, border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0 }} />
            <span onClick={canOpen ? () => setDetailRow(r) : undefined} title={canOpen ? 'Open details' : undefined}
              style={{ fontWeight: 600, color: BRIO_GOLD, cursor: canOpen ? 'pointer' : 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
          </span>
        );
      }
      case 'code':
        return <EditableCell value={r.code} type="text" onChange={v => save(r.id, { projectCode: v })} />;
      case 'client':
        return <EditableCell value={r.p.clientId ?? ''} type="select" options={clientOptions}
          displayValue={r.client} onChange={v => save(r.id, { clientId: v === '' ? null : Number(v) })} />;
      case 'category':
        return <EditableCell value={r.category} type="select" options={BRIO_PROJECT_TYPES} onChange={v => save(r.id, { category: v })} />;
      case 'status':
        return <EditableCell value={r.status} type="select" options={STATUS_OPTIONS} onChange={v => save(r.id, { status: v })}
          displayValue={<StatusBadge status={r.status} getProjectStatusStyle={getProjectStatusStyle} />} />;
      case 'supervisors': {
        // Assigning supervisors is admin-only; supervisors see read-only names.
        if (!isAdmin) {
          return r.supervisorNames.length
            ? <span title={r.supervisorNames.join(', ')} style={ellipsis}>{r.supervisorNames.join(', ')}</span>
            : '—';
        }
        // Options = eligible (active supervisors/admins) + any already-assigned
        // user who isn't eligible, so existing assignments still render and can
        // be removed.
        const extras = r.supervisorIds
          .filter(id => !eligibleSupervisorIds.has(id))
          .map(id => ({ value: id, label: (userName.get(id) || `#${id}`) + ' (not a supervisor)' }));
        const opts = extras.length ? [...supervisorOptions, ...extras] : supervisorOptions;
        return <MultiSelectCell selected={r.supervisorIds} options={opts} minWidth={210}
          onChange={ids => save(r.id, { supervisors: ids })} />;
      }
      case 'currency':
        return <EditableCell value={r.currency} type="select" options={CURRENCY_OPTIONS} onChange={v => save(r.id, { contractCurrency: v })} />;
      case 'budget':
        return <EditableCell value={r.budget || ''} type="money" align="right" onChange={v => saveWithFeeRevision(r, { budget: Number(v) || 0 })} displayValue={fmtMoney(r.budget, r.currency)} />;
      case 'targetCB':
        return <EditableCell value={r.targetCB || ''} type="money" align="right" onChange={v => save(r.id, { targetConstructionBudget: Number(v) || 0 })} displayValue={fmtMoney(r.targetCB, r.currency)} />;
      case 'finalCB':
        return <EditableCell value={r.finalCB || ''} type="money" align="right" onChange={v => save(r.id, { finalConstructionBudget: Number(v) || 0 })} displayValue={fmtMoney(r.finalCB, r.currency)} />;
      case 'projectSize':
        return <EditableCell value={r.projectSize ?? ''} type="number" align="right" onChange={v => save(r.id, { projectSize: v === '' ? 0 : Number(v) })} displayValue={fmtNum(r.projectSize)} />;
      case 'plotArea':
        return <EditableCell value={r.plotArea ?? ''} type="number" align="right" onChange={v => save(r.id, { plotArea: v === '' ? null : Number(v) })} displayValue={fmtNum(r.plotArea)} />;
      case 'plannedHours':
        return <EditableCell value={r.plannedHours ?? ''} type="number" align="right" onChange={v => save(r.id, { plannedHours: v === '' ? 0 : Number(v) })} displayValue={fmtNum(r.plannedHours)} />;
      case 'timeline':
        return r.timeStart ? <span style={{ fontSize: 10.5 }}>{fmtDate(r.timeStart)} – {r.timeEnd ? fmtDate(r.timeEnd) : '…'}</span> : '—';
      case 'address': {
        const disp = r.address ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ ...ellipsis, maxWidth: 120 }}>{r.address}</span>
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.address)}`} target="_blank" rel="noreferrer" title="Open in Google Maps" onClick={e => e.stopPropagation()} style={{ color: BRIO_GOLD, display: 'inline-flex' }}><ExternalLink size={11} /></a>
          </span>
        ) : null;
        return <EditableCell value={r.address} type="text" onChange={v => save(r.id, { projectAddress: v })} displayValue={disp} />;
      }
      case 'city':
        return <EditableCell value={r.city} type="text" placeholder="City" onChange={v => save(r.id, { city: v })} />;
      case 'country':
        return <EditableCell value={r.country} type="text" placeholder="Country" onChange={v => save(r.id, { country: v })} />;
      case 'elevation':
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', width: '100%' }}>
            <EditableCell value={r.elevationM ?? ''} type="number" align="right"
              onChange={v => save(r.id, { elevationM: v === '' ? null : Number(v), elevationSource: 'manual' })}
              displayValue={r.elevationM != null ? Math.round(r.elevationM) : (r.coords ? '' : '—')} />
            {r.coords && r.elevationM == null && !fetchState.running && (
              <button title="Fetch elevation" onClick={() => runElevationFetch([r])}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_GREEN, padding: 0, display: 'inline-flex' }}>
                <Mountain size={12} />
              </button>
            )}
          </span>
        );
      case 'climate':
        return <ClimateCell value={r.climate} suggestion={r.suggestion} onChange={v => save(r.id, { climateZone: v })} />;
      case 'siteTypes':
        return <MultiSelectCell selected={r.siteTypes} options={SITE_TYPE_OPTIONS} onChange={v => save(r.id, { siteTypes: v })} />;
      case 'climateTemp': {
        const cn = r.climateNormals;
        if (cn && (cn.winterDayC != null || cn.summerDayC != null)) {
          const t = (d, n) => `${d != null ? Math.round(d) : '–'}°/${n != null ? Math.round(n) : '–'}°`;
          const w = t(cn.winterDayC, cn.winterNightC), s = t(cn.summerDayC, cn.summerNightC);
          return (
            <span title={`Winter day/night ${w} · Summer day/night ${s} — avg daily high/low${cn.period ? `, ${cn.period}` : ''}`} style={{ fontSize: 10.5, whiteSpace: 'nowrap' }}>
              <span style={{ color: '#4f7396' }}>{w}</span> <span style={{ color: BRIO_GREY }}>·</span> <span style={{ color: '#b9702f' }}>{s}</span>
            </span>
          );
        }
        if (r.coords && !climateFetch.running) {
          return (
            <button title="Fetch seasonal climate data" onClick={() => runClimateFetch([r])}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_GREEN, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontFamily: FONT }}>
              <CloudRain size={12} /> get
            </button>
          );
        }
        return '—';
      }
      case 'climatePrecip': {
        const cn = r.climateNormals;
        if (cn && (cn.winterPrecipMm != null || cn.summerPrecipMm != null)) {
          return (
            <span title={`Winter / Summer avg seasonal precipitation (mm)${cn.period ? `, ${cn.period}` : ''}`} style={{ fontSize: 10.5, whiteSpace: 'nowrap' }}>
              {cn.winterPrecipMm ?? '–'} / {cn.summerPrecipMm ?? '–'}
            </span>
          );
        }
        return '—';
      }
      // Soil and Bedrock read the mapped datasets, but either can be overridden
      // by hand — laterite is the standing example, being a weathering profile
      // no geological map carries. Both cells edit in place: type to override,
      // clear to fall back to the mapped reading, which stays in the tooltip.
      case 'soil': {
        const soil = r.subsurface?.soil;
        const mapped = resolveSoil(r.subsurface, null).text;
        if (!r.soil && !r.subsurface) return subsurfaceGetCell(r);
        return (
          <SubsurfaceCell
            text={r.soil} manual={r.soilManual} color="#7a6a3f"
            lines={[
              r.soil,
              r.soilManual
                ? `Set by hand${mapped ? ` — mapped reading: ${mapped}` : ' — no mapped reading for this site'}`
                : null,
              describeSoilSlice(soil?.surface),
              describeSoilSlice(soil?.deep),
              soil?.source ? `${soil.source} · 250 m grid — regional context, not a site investigation.` : null,
            ].filter(Boolean)}
            onChange={v => save(r.id, { subsurfaceOverride: withOverride(r.subsurfaceOverride, 'soil', v) })}
            onRevert={() => save(r.id, { subsurfaceOverride: withOverride(r.subsurfaceOverride, 'soil', '') })}
          />
        );
      }
      case 'bedrock': {
        const b = r.subsurface?.bedrock;
        const mapped = resolveBedrock(r.subsurface, null).text;
        if (!r.bedrock && !r.subsurface) return subsurfaceGetCell(r);
        return (
          <SubsurfaceCell
            text={r.bedrock} manual={r.bedrockManual} color="#5a6b7a"
            lines={[
              r.bedrock,
              r.bedrockManual
                ? `Set by hand${mapped ? ` — mapped reading: ${mapped}` : ' — no mapped reading for this site'}`
                : null,
              b?.unit,
              // The cell shows our plain reading of the map's wording; the
              // wording itself stays one tap away.
              bedrockSourceTerms(r.subsurface),
              b?.description,
              b ? `${b.source || 'macrostrat'} · mapped at regional scale — not a borehole log.` : null,
            ].filter(Boolean)}
            onChange={v => save(r.id, { subsurfaceOverride: withOverride(r.subsurfaceOverride, 'bedrock', v) })}
            onRevert={() => save(r.id, { subsurfaceOverride: withOverride(r.subsurfaceOverride, 'bedrock', '') })}
          />
        );
      }
      case 'invoiceName':
        return <EditableCell value={r.invoiceName} type="text" placeholder="(uses name)" onChange={v => save(r.id, { invoiceProjectName: v })} />;
      case 'email':
        return <EditableCell value={r.email} type="text" onChange={v => save(r.id, { projectEmailId: v })} />;
      case 'comms':
        return (
          <button onClick={() => save(r.id, { commsEnabled: !r.comms })} title="Toggle comms"
            style={{ display: 'inline-block', fontSize: 10, padding: '1px 6px', borderRadius: 3, cursor: 'pointer', fontFamily: FONT, fontWeight: 500, border: '1px solid ' + (r.comms ? BRIO_GREEN + '55' : BRIO_BORDER), background: r.comms ? BRIO_GREEN + '18' : '#f0f0ed', color: r.comms ? BRIO_GREEN : BRIO_GREY }}>
            {r.comms ? 'On' : 'Off'}
          </button>
        );
      case 'updated':
        return <span style={{ fontSize: 10.5, color: BRIO_GREY }}>{fmtDate(r.updatedAt)}</span>;
      case 'details':
        // Supervisors can only open the card for projects they supervise.
        if (!canOpenDetail(r)) return <span style={{ color: BRIO_GREY }}>—</span>;
        return (
          <button onClick={() => setDetailRow(r)} title={isAdmin ? 'Open / edit project details' : 'Open project details'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, padding: '2px 7px', borderRadius: 3, cursor: 'pointer', fontFamily: FONT, fontWeight: 500, border: '1px solid ' + BRIO_BORDER, background: '#fff', color: BRIO_GOLD }}>
            <Pencil size={11} /> {isAdmin ? 'Edit' : 'View'}
          </button>
        );
      default: return '—';
    }
  };

  // ══════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════
  return (
    <div style={{ fontFamily: FONT, padding: '16px 20px' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: 0 }}>Projects Overview</h2>
        <span style={{ fontSize: 11, color: BRIO_GREY }}>{displayRows.length} of {rows.length}</span>

        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
          <Search size={12} style={{ color: '#888', position: 'absolute', left: 6 }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
            style={{ fontSize: 11, padding: '4px 6px 4px 22px', border: '1px solid ' + BRIO_BORDER, borderRadius: 4, fontFamily: FONT, width: 160 }} />
          {search && <button onClick={() => setSearch('')} style={{ ...xBtnStyle, color: '#888' }}><X size={12} /></button>}
        </div>

        <div style={{ position: 'relative' }}>
          <ToolbarIcon onClick={() => { setShowSort(s => !s); setShowFilter(false); }} tooltip="Sort" active={showSort || sortRules.length > 0} badge={sortRules.length || null}>
            <ArrowUpDown size={12} /> Sort
          </ToolbarIcon>
          {showSort && <SortPanel sortRules={sortRules} onChange={setSortRules} onClose={() => setShowSort(false)} sortableCols={sortableCols} />}
        </div>
        <div style={{ position: 'relative' }}>
          <ToolbarIcon onClick={() => { setShowFilter(f => !f); setShowSort(false); }} tooltip="Filter" active={showFilter || filterRules.length > 0} badge={filterRules.length || null}>
            <Filter size={12} /> Filter
          </ToolbarIcon>
          {showFilter && <FilterPanel filterRules={filterRules} onChange={setFilterRules} onClose={() => setShowFilter(false)} uniqueValues={uniqueValues} filterableCols={filterableCols} />}
        </div>

        <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {fetchState.running && (
            <span style={{ fontSize: 11, color: BRIO_GREY }}>Fetching elevation… {fetchState.done}/{fetchState.total}</span>
          )}
          {climateFetch.running && (
            <span style={{ fontSize: 11, color: BRIO_GREY }}>Fetching climate… {climateFetch.done}/{climateFetch.total}</span>
          )}
          {subsurfaceFetch.running && (
            <span style={{ fontSize: 11, color: BRIO_GREY }}>
              Fetching subsurface… {subsurfaceFetch.done}/{subsurfaceFetch.total} (the soil service is rate-limited, so this is slow)
            </span>
          )}
          <ToolbarIcon onClick={() => runElevationFetch(elevationCandidates)} tooltip="Fetch site elevation for coordinate-bearing projects" badge={elevationCandidates.length || null}>
            <Mountain size={12} /> Elevation
          </ToolbarIcon>
          <ToolbarIcon onClick={() => runClimateFetch(climateCandidates)} tooltip="Fetch seasonal climate normals (Open-Meteo) for coordinate-bearing projects" badge={climateCandidates.length || null}>
            <CloudRain size={12} /> Climate
          </ToolbarIcon>
          <ToolbarIcon onClick={() => runSubsurfaceFetch(subsurfaceCandidates)} tooltip="Fetch regional soil texture (SoilGrids) and bedrock geology (Macrostrat) for coordinate-bearing projects — regional context, not site data" badge={subsurfaceCandidates.length || null}>
            <Layers size={12} /> Subsurface
          </ToolbarIcon>
        </div>
      </div>

      {error && (
        <div style={{ fontSize: 11, color: BRIO_RED, background: BRIO_RED + '12', border: '1px solid ' + BRIO_RED + '30', borderRadius: 4, padding: '6px 10px', marginBottom: 8 }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ fontSize: 11, color: BRIO_GOLD, background: BRIO_GOLD + '10', border: '1px solid ' + BRIO_GOLD + '35', borderRadius: 4, padding: '6px 10px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1 }}>{notice}</span>
          <button onClick={() => setNotice('')} title="Dismiss" style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRIO_GOLD, padding: 0, display: 'inline-flex' }}><X size={12} /></button>
        </div>
      )}

      {/* Table — scroll container carries sticky header + frozen columns */}
      <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', position: 'relative', border: '1px solid ' + BRIO_BORDER, borderRadius: 6 }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            {orderedColumns.map(col => {
              const w = collapsedCols[col.key] ? COLLAPSED_W : colWidthOf(col, colWidths);
              return <col key={col.key} style={{ width: w + 'px', minWidth: (collapsedCols[col.key] ? COLLAPSED_W : col.minW) + 'px' }} />;
            })}
          </colgroup>
          <thead>
            <tr>
              {orderedColumns.map((col, colIdx) => {
                const sortRule = sortRules.find(r => r.key === col.key);
                const isCollapsed = collapsedCols[col.key];
                const isFrozen = col.alwaysFrozen || frozenCols[col.key];
                const frozenLeft = isFrozen ? computeFrozenLeft(orderedColumns, collapsedCols, frozenCols, colWidths, colIdx) : undefined;
                const stickyStyle = isFrozen ? { position: 'sticky', left: frozenLeft + 'px', zIndex: 12, borderRight: '2px solid ' + BRIO_GOLD + '40' } : {};
                const isDragOver = dragOverColIdx === colIdx && dragColIdxRef.current !== colIdx;

                if (isCollapsed) {
                  return (
                    <th key={col.key} style={{ ...HEADER_STYLE, ...stickyStyle, padding: '6px 2px', textAlign: 'center' }}
                      onClick={() => toggleColCollapse(col.key)} title={'Expand ' + col.label}>
                      <ChevronRight size={11} style={{ color: BRIO_GREY }} />
                    </th>
                  );
                }
                return (
                  <th key={col.key}
                    data-col-idx={colIdx}
                    draggable={!col.alwaysFrozen && !frozenCols[col.key]}
                    onDragStart={e => {
                      // A width-resize in progress must not turn into a reorder-drag.
                      if (resizeRef.current || col.alwaysFrozen || frozenCols[col.key]) { e.preventDefault(); return; }
                      dragColIdxRef.current = colIdx;
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', String(colIdx));
                    }}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverColIdx(colIdx); }}
                    onDragLeave={() => setDragOverColIdx(null)}
                    onDrop={e => {
                      e.preventDefault();
                      const fromIdx = dragColIdxRef.current;
                      if (fromIdx != null && fromIdx !== colIdx) moveColumn(fromIdx, colIdx);
                      dragColIdxRef.current = null; setDragOverColIdx(null);
                    }}
                    onDragEnd={() => { dragColIdxRef.current = null; setDragOverColIdx(null); }}
                    onTouchStart={() => handleTouchStart(colIdx, col)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    style={{
                      ...HEADER_STYLE, ...stickyStyle,
                      textAlign: col.num ? 'right' : 'left',
                      borderLeft: isDragOver ? '2px solid ' + BRIO_GOLD : undefined,
                      touchAction: (!col.alwaysFrozen && !frozenCols[col.key]) ? 'none' : undefined,
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: col.num ? 'flex-end' : 'space-between' }}>
                      <span onClick={() => handleColumnSort(col.key)} title={col.tooltip || undefined}
                        style={{ cursor: col.sortable ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                        {col.label}
                        {sortRule && (sortRule.dir === 'asc'
                          ? <ChevronUp size={9} style={{ color: BRIO_GOLD, flexShrink: 0 }} />
                          : <ChevronDown size={9} style={{ color: BRIO_GOLD, flexShrink: 0 }} />)}
                      </span>
                      {!col.alwaysFrozen && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                          <span onClick={e => { e.stopPropagation(); toggleFreezeCol(col.key); }}
                            style={{ cursor: 'pointer', display: 'inline-flex', padding: 1, color: frozenCols[col.key] ? BRIO_GOLD : BRIO_GREY, opacity: frozenCols[col.key] ? 1 : 0.4 }}
                            title={frozenCols[col.key] ? 'Unfreeze column' : 'Freeze column'}>
                            {frozenCols[col.key] ? <Lock size={10} /> : <Unlock size={10} />}
                          </span>
                          <span onClick={e => { e.stopPropagation(); toggleColCollapse(col.key); }}
                            style={{ cursor: 'pointer', display: 'inline-flex', padding: 1, color: BRIO_GREY }}
                            title="Collapse column">
                            <ChevronLeft size={11} />
                          </span>
                        </span>
                      )}
                    </span>
                    {/* Drag the right edge to resize; double-click to reset. */}
                    <div
                      onMouseDown={e => startColResize(e, col)}
                      onClick={e => e.stopPropagation()}
                      onDoubleClick={e => { e.stopPropagation(); resetColWidth(col.key); }}
                      onDragStart={e => e.preventDefault()}
                      draggable={false}
                      title="Drag to resize · double-click to reset"
                      style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 7, cursor: 'col-resize', zIndex: 13, userSelect: 'none' }}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((r, i) => (
              <tr key={r.id} style={{ background: i % 2 ? BRIO_BG : '#fff' }}>
                {orderedColumns.map((col, colIdx) => {
                  const isFrozen = col.alwaysFrozen || frozenCols[col.key];
                  const frozenLeft = isFrozen ? computeFrozenLeft(orderedColumns, collapsedCols, frozenCols, colWidths, colIdx) : undefined;
                  const rowBg = i % 2 ? BRIO_BG : '#fff';
                  const frozenTdStyle = isFrozen ? { position: 'sticky', left: frozenLeft + 'px', zIndex: 5, background: rowBg, borderRight: '2px solid ' + BRIO_GOLD + '40' } : {};
                  if (collapsedCols[col.key]) return <td key={col.key} style={{ ...tdStyle, padding: '5px 0', ...frozenTdStyle }} />;
                  return (
                    <td key={col.key} style={{ ...tdStyle, textAlign: col.num ? 'right' : 'left', ...frozenTdStyle }}>
                      {renderCell(col, r)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {displayRows.length === 0 && (
              <tr><td colSpan={orderedColumns.length} style={{ ...tdStyle, textAlign: 'center', color: BRIO_GREY, padding: 20 }}>No projects match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detailRowLive && canOpenDetail(detailRowLive) && (
        <ProjectDetailCard
          row={detailRowLive}
          onClose={() => setDetailRow(null)}
          onSaveFee={saveWithFeeRevision}
          canEditFees={isAdmin}
        />
      )}
    </div>
  );
}

function StatusBadge({ status, getProjectStatusStyle }) {
  // getProjectStatusStyle returns { bg, text, border (Tailwind classes), label, color (hex) };
  // use the hex to build an inline tinted badge.
  const st = getProjectStatusStyle ? getProjectStatusStyle(status) : {};
  const color = st.color || BRIO_GREY;
  return (
    <span style={{ display: 'inline-block', fontSize: 10, padding: '1px 6px', borderRadius: 3, fontWeight: 500, textTransform: 'capitalize', background: color + '1A', color, border: '1px solid ' + color + '55' }}>
      {st.label || status}
    </span>
  );
}

const ellipsis = { display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' };
const HEADER_STYLE = { fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', color: BRIO_GREY, background: '#f4f3ee', padding: '7px 8px', borderBottom: '1px solid ' + BRIO_BORDER, position: 'sticky', top: 0, zIndex: 10, whiteSpace: 'nowrap', userSelect: 'none' };
const tdStyle = { fontSize: 11, color: '#1e293b', padding: '5px 8px', borderBottom: '1px solid ' + BRIO_BORDER, verticalAlign: 'middle', whiteSpace: 'nowrap' };
